import React from 'react'

import LinkWithState from '@/components/base/link'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'

import type { LucideIcon } from 'lucide-react'

/**
 * The one view for "nothing here". Every no-data state in the app goes
 * through it, so they read alike: what will appear here, why it is worth
 * having, and the single next step that gets there.
 *
 * - `page`: the whole screen is empty; centred in the free space.
 * - `section`: a block inside a screen (a widget, a list in a card).
 * - `inline`: one quiet line inside a widget (a stat with no data yet).
 *
 * `kind` sets the tone: a first use leads with the action in the brand
 * tint; waiting, cleared and no-results stay neutral; unavailable is a
 * failure with a way to retry.
 */
type EmptyKind =
    | 'first-use'
    | 'no-results'
    | 'cleared'
    | 'waiting'
    | 'unavailable'

type EmptyAction = {
    label: React.ReactNode
    /** A destination (kept in the back stack), or a handler. */
    to?: string
    onClick?: () => void
}

type EmptyStateProps = {
    icon: LucideIcon
    title?: React.ReactNode
    description?: React.ReactNode
    /** The one next step. */
    action?: EmptyAction
    /** A quieter alternative, for no-results and first use only. */
    secondaryAction?: EmptyAction
    variant?: 'page' | 'section' | 'inline'
    kind?: EmptyKind
    className?: string
}

const INLINE_ACTION =
    'ml-auto shrink-0 text-xs font-medium text-primary underline-offset-2 hover:underline'

function ActionButton({
    action,
    tone,
    size
}: {
    action: EmptyAction
    /** strong: the state's point; normal: the main step; quiet: an aside. */
    tone: 'strong' | 'normal' | 'quiet'
    size: 'sm' | 'default'
}) {
    const button = (
        <Button
            size={size}
            variant={
                tone === 'strong'
                    ? 'default'
                    : tone === 'normal'
                      ? 'outline'
                      : 'ghost'
            }
            className={cn('rounded-xl', tone !== 'quiet' && 'min-w-36')}
            onClick={action.onClick}
        >
            {action.label}
        </Button>
    )
    return action.to ? (
        <LinkWithState to={action.to}>{button}</LinkWithState>
    ) : (
        button
    )
}

function EmptyState({
    icon: Icon,
    title,
    description,
    action,
    secondaryAction,
    variant = 'page',
    kind = 'first-use',
    className
}: EmptyStateProps) {
    const lead = kind === 'first-use'
    // A failure is announced; an empty list is not urgent.
    const role = kind === 'unavailable' ? 'alert' : 'status'
    // The action leads where it is the point of the state.
    const strongAction = lead || kind === 'no-results' || kind === 'unavailable'
    const iconTone = lead
        ? 'bg-primary/10 text-primary'
        : kind === 'unavailable'
          ? 'bg-destructive/10 text-destructive'
          : 'bg-muted text-muted-foreground'

    if (variant === 'inline') {
        return (
            <div
                role={role}
                className={cn(
                    'flex items-center gap-2 text-muted-foreground',
                    className
                )}
            >
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <Typography variant="body-xs" className="text-muted-foreground">
                    {title ?? description}
                </Typography>
                {action &&
                    (action.to ? (
                        <LinkWithState to={action.to} className={INLINE_ACTION}>
                            {action.label}
                        </LinkWithState>
                    ) : (
                        <button
                            type="button"
                            onClick={action.onClick}
                            className={INLINE_ACTION}
                        >
                            {action.label}
                        </button>
                    ))}
            </div>
        )
    }

    const section = variant === 'section'
    return (
        <div
            role={role}
            className={cn(
                section
                    ? 'flex items-start gap-3 rounded-2xl border border-dashed border-border bg-muted/30 p-4'
                    : 'flex min-h-[55vh] flex-col items-center justify-center gap-3 px-6 py-10 text-center',
                className
            )}
        >
            <div
                data-testid="empty-state-icon"
                className={cn(
                    'flex items-center justify-center',
                    section
                        ? 'h-10 w-10 shrink-0 rounded-xl'
                        : 'h-14 w-14 rounded-2xl',
                    iconTone
                )}
            >
                <Icon
                    className={section ? 'h-5 w-5' : 'h-6 w-6'}
                    aria-hidden="true"
                />
            </div>
            <div
                className={
                    section ? 'flex min-w-0 flex-1 flex-col gap-1' : 'contents'
                }
            >
                {title ? (
                    <Typography
                        variant={section ? 'title-sm' : 'title-md'}
                        className={section ? undefined : 'text-balance'}
                    >
                        {title}
                    </Typography>
                ) : null}
                {description ? (
                    <Typography
                        variant="body-sm"
                        className={cn(
                            'text-muted-foreground',
                            !section && 'max-w-xs text-pretty'
                        )}
                    >
                        {description}
                    </Typography>
                ) : null}
                {(action || secondaryAction) && (
                    <div
                        className={
                            section
                                ? 'mt-2 flex flex-wrap items-center gap-2'
                                : 'mt-2 flex flex-col items-center gap-1'
                        }
                    >
                        {action && (
                            <ActionButton
                                action={action}
                                tone={strongAction ? 'strong' : 'normal'}
                                size={section ? 'sm' : 'default'}
                            />
                        )}
                        {secondaryAction && (
                            <ActionButton
                                action={secondaryAction}
                                tone="quiet"
                                size="sm"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default EmptyState
